import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, KeyRound, User as UserIcon, Mail } from 'lucide-react';
import { toast } from 'sonner';

function MyProfile({ user }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();

    // Build payload — only include fields that changed
    const payload = {};
    if (form.full_name && form.full_name !== user.full_name) {
      payload.full_name = form.full_name;
    }
    if (form.email && form.email.toLowerCase() !== user.email.toLowerCase()) {
      payload.email = form.email;
    }

    const wantsPasswordChange = form.new_password || form.current_password;
    if (wantsPasswordChange) {
      if (!form.current_password) {
        toast.error('Informe sua senha atual para alterar a senha');
        return;
      }
      if (form.new_password !== form.confirm_password) {
        toast.error('As senhas não coincidem');
        return;
      }
      if (form.new_password.length < 8) {
        toast.error('A nova senha deve ter pelo menos 8 caracteres');
        return;
      }
      payload.current_password = form.current_password;
      payload.new_password = form.new_password;
    }

    if (Object.keys(payload).length === 0) {
      toast.info('Nenhum dado foi alterado');
      return;
    }

    setSaving(true);
    try {
      await api.put('/auth/me/profile', payload);
      toast.success('Cadastro atualizado com sucesso!');
      // Clear password fields
      setForm((p) => ({ ...p, current_password: '', new_password: '', confirm_password: '' }));
      // If email or name changed, force re-fetch by clearing stored user
      if (payload.email || payload.full_name) {
        sessionStorage.removeItem('user');
        // small delay so user sees the toast
        setTimeout(() => window.location.reload(), 800);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao atualizar cadastro';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Meu Cadastro</h1>
          <p className="text-gray-600 mt-1">
            Atualize seu nome, email ou altere sua senha.
          </p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <Card className="p-6 space-y-4" data-testid="profile-info-card">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <UserIcon className="w-5 h-5" />
              <h2 className="font-semibold">Dados pessoais</h2>
            </div>

            <div>
              <Label htmlFor="full_name">Nome completo</Label>
              <Input
                id="full_name"
                data-testid="input-full-name"
                value={form.full_name}
                onChange={onChange('full_name')}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  className="pl-9"
                  value={form.email}
                  onChange={onChange('email')}
                  required
                />
              </div>
            </div>

            <div className="text-xs text-gray-500 pt-2">
              Perfil: <strong className="text-gray-700">
                {user?.role === 'ADMIN' ? 'Administrador' : user?.role === 'CONSULTANT' ? 'Consultor' : 'Cliente'}
              </strong>
            </div>
          </Card>

          <Card className="p-6 space-y-4" data-testid="profile-password-card">
            <div className="flex items-center gap-2 text-emerald-700 mb-2">
              <KeyRound className="w-5 h-5" />
              <h2 className="font-semibold">Alterar senha</h2>
            </div>
            <p className="text-sm text-gray-600">
              Preencha apenas se quiser alterar sua senha. A nova senha deve ter no mínimo 8 caracteres.
            </p>

            <div>
              <Label htmlFor="current_password">Senha atual</Label>
              <Input
                id="current_password"
                type="password"
                data-testid="input-current-password"
                autoComplete="current-password"
                value={form.current_password}
                onChange={onChange('current_password')}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new_password">Nova senha</Label>
                <Input
                  id="new_password"
                  type="password"
                  data-testid="input-new-password"
                  autoComplete="new-password"
                  value={form.new_password}
                  onChange={onChange('new_password')}
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirmar nova senha</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  data-testid="input-confirm-password"
                  autoComplete="new-password"
                  value={form.confirm_password}
                  onChange={onChange('confirm_password')}
                />
              </div>
            </div>
          </Card>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="btn-cancel-profile"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="btn-save-profile"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default MyProfile;
